import dateFormat from '@fhnw/components/DateFormat/dateFormat';
import { Link } from '@fhnw/components/Link/Link';
// eslint-disable-next-line import/no-unresolved
import { Content } from '@plone/types';
import UniversalLink from '@plone/volto/components/manage/UniversalLink/UniversalLink';
import {
  formatInfoItem,
  occasionVisible,
  nextOccasion,
} from '@fhnw/helpers/DesignSystem/cardTransformers';
import {
  normalizeContent,
  normalizeLink,
} from '@fhnw/helpers/DesignSystem/normalizeContent';
import RichText from '@fhnw/components/RichText/RichText';
import { messages } from './messages';
import type { ComponentType } from 'react';

type FieldValue = string | ComponentType<any>;

export interface Field {
  label: { id: string; defaultMessage: string };
  value: FieldValue;
  html?: boolean;
  joinComma?: boolean;
}

export type FieldGroupName =
  | 'DegreeProgram'
  | 'ContinuingEducation'
  | 'Project'
  | 'FHNWEvent'
  | 'InfoEvent';

export type FieldsConfig = Record<FieldGroupName, Field[]>;

const EctsPoints = (content: Content, intl: any) => {
  return [content?.ects_points, content?.ects]
    .filter((val: any) => !!val)
    .join(' / ');
};

const NextStart = (content: Content, intl: any) => {
  const occasion = nextOccasion(content?.occasions, content);
  if (!occasion || (!occasion.occasion_start && !occasion.rolling_intake)) {
    return normalizeContent(content?.next_start_fallback, true);
  }
  if (!occasion.occasion_start && occasion.rolling_intake) {
    return intl.formatMessage(messages.RollingIntake);
  }
  return dateFormat({
    start: new Date(occasion.occasion_start),
    dateOnly: true,
    locale: intl.locale,
  });
};

const Documents = (content: Content, intl: any) => {
  const documents = content?.documents?.filter((doc) => doc !== null) || [];
  if (documents.length <= 0) {
    return null;
  }
  return (
    <ul className="unstyled">
      {documents.map((doc, i) => (
        <li key={i}>
          <Link href={normalizeLink(doc)} linkComponent={UniversalLink}>
            {doc?.title}
          </Link>
          {doc?.description && <small> - {doc?.description}</small>}
        </li>
      ))}
    </ul>
  );
};

const Gtc = (content: Content, intl: any) => {
  if (!content?.terms_and_conditions) {
    return null;
  }

  return (
    <Link
      href={normalizeLink(content.terms_and_conditions)}
      linkComponent={UniversalLink}
    >
      {intl.formatMessage(messages.ToGTC)}
    </Link>
  );
};

interface OccasionInfo {
  id: number;
  date: string | Date;
  info: string;
}

const Occasions = (content: Content, intl: any) => {
  if (!content?.occasions) {
    return null;
  }

  const items: OccasionInfo[] = [];
  content.occasions.forEach((occasion, i) => {
    if (!occasionVisible(occasion, content)) {
      return;
    }

    const info: string[] = [];
    if (occasion?.occasion_start) {
      info.push(
        dateFormat({
          start: new Date(occasion.occasion_start),
          end: occasion?.occasion_end
            ? new Date(occasion.occasion_end)
            : undefined,
          openEnd: occasion?.open_end,
          wholeDay: occasion?.whole_day,
          locale: intl.locale,
        }),
      );
    }
    if (occasion?.rolling_intake) {
      info.push(intl.formatMessage(messages.RollingIntake));
    }
    if (occasion?.place) {
      info.push(occasion.place.replaceAll('\n', ', '));
    }
    if (occasion?.duration) {
      info.push(occasion.duration);
    }

    items.push({
      id: i,
      date: occasion?.occasion_start,
      info: info.join(', '),
    });
  });

  if (!items.length) {
    return null;
  }

  items.sort((x, y) => {
    if (x.date > y.date) return 1;
    if (x.date < y.date) return -1;
    return x.id > y.id ? 1 : -1;
  });

  return (
    <ul>
      {items.map(({ id, info }) => (
        <li key={id}>{info}</li>
      ))}
    </ul>
  );
};

const Address = (content: Content, intl: any) => {
  const address = `${content?.location_address?.address || ''}\n${content?.location_address?.zipcode || ''} ${content?.location_address?.city || ''}`;

  if (!address.trim()) {
    return null;
  }

  return (
    <address>
      <RichText text={address} />
    </address>
  );
};

const extractTitles = (items: any) => {
  if (!items) return [];

  if (!Array.isArray(items)) {
    items = [items];
  }

  return items
    .map((item: any) => (typeof item === 'object' ? item?.title : item))
    .filter(Boolean);
};

const mergeUniversityFields = (content: Content) => {
  const valuesUniversities = extractTitles(content?.universities).join(', ');
  const valuesOrganisationEntities = extractTitles(
    content?.organisation_entities,
  ).join(', ');

  if (valuesUniversities.length && valuesOrganisationEntities.length) {
    return valuesUniversities.concat(' / ').concat(valuesOrganisationEntities);
  }

  return valuesUniversities.length
    ? valuesUniversities
    : valuesOrganisationEntities;
};

const fields: FieldsConfig = {
  DegreeProgram: [
    { label: messages.Degree, value: 'degree' },
    { label: messages.StudyMode, value: 'study_mode', joinComma: true },
    {
      label: messages.LearningEnvironment,
      value: 'learning_environment',
      joinComma: true,
    },
    { label: messages.ECTSPoints, value: EctsPoints },
    { label: messages.NextStart, value: NextStart },
    { label: messages.Duration, value: 'duration' },
    { label: messages.Language, value: 'language_description', html: true },
    { label: messages.Place, value: 'location_text' },
    { label: messages.StayingAbroad, value: 'staying_abroad' },
    { label: messages.ApplicationFee, value: 'application_fee', html: true },
    { label: messages.SemesterFee, value: 'semester_fee', html: true },
    { label: messages.Documents, value: Documents },
    { label: messages.GTC, value: Gtc },
  ],
  ContinuingEducation: [
    { label: messages.Degree, value: 'degree' },
    {
      label: messages.LearningEnvironment,
      value: 'learning_environment',
      joinComma: true,
    },
    { label: messages.ECTSPoints, value: EctsPoints },
    { label: messages.NextStart, value: NextStart },
    { label: messages.Duration, value: 'duration' },
    {
      label: messages.TeachingLanguages,
      value: 'language_description',
      html: true,
    },
    { label: messages.Venues, value: 'location_text' },
    { label: messages.Fee, value: 'fee', html: true },
    { label: messages.AdmissionRequirements, value: 'admission_requirements' },
    { label: messages.FinancingTerms, value: 'financing_terms' },
    { label: messages.EducationFormat, value: 'education_format' },
    { label: messages.TargetAudience, value: 'target_audience' },
    { label: messages.CostAbsorption, value: 'cost_absorption' },
    { label: messages.Documents, value: Documents },
    { label: messages.GTC, value: Gtc },
    { label: messages.ProductCode, value: 'product_code' },
  ],
  Project: [
    { label: messages.Type, value: 'project_type' },
    { label: messages.ResearchAreas, value: 'research_areas' },
    { label: messages.Topics, value: 'topics' },
    { label: messages.University, value: mergeUniversityFields },
    { label: messages.Partner, value: 'partner', html: true },
    { label: messages.Funding, value: 'project_funding', html: true },
    { label: messages.Volume, value: 'project_volume', html: true },
    { label: messages.RunningTime, value: 'duration', html: true },
    { label: messages.Management, value: 'project_management', html: true },
    {
      label: messages.Collaboration,
      value: 'project_collaboration',
      html: true,
    },
  ],
  FHNWEvent: [
    { label: messages.OrganizedBy, value: 'organized_by', html: true },
    {
      label: messages.EventLanguage,
      value: 'language_description',
      html: true,
    },
    { label: messages.Expenses, value: 'expenses' },
    { label: messages.Address, value: Address },
    { label: messages.Occasions, value: Occasions },
  ],
  InfoEvent: [],
};

fields['InfoEvent'] = fields.FHNWEvent;

const calculateKeyData = (content: Content, intl: any) => {
  const info = [] as Array<{
    key: string;
    value: React.ReactNode | string;
    structured?: boolean;
  }>;

  if (content?.['@type'] in fields) {
    fields[content?.['@type'] as keyof typeof fields].forEach(
      ({
        label,
        value,
        html,
        joinComma,
      }: {
        label: any;
        value: any;
        html?: boolean;
        joinComma?: boolean;
      }) => {
        if (typeof value === 'string') {
          value = normalizeContent(
            content?.[value as keyof typeof content],
            true,
          );
        } else {
          value = value(content, intl);
        }
        if (!value) {
          return;
        }

        if (Array.isArray(value)) {
          const tokens = [] as string[];
          value.forEach((val) => {
            if (typeof val === 'object') {
              val = val?.title;
            }
            if (typeof val === 'string' && val) {
              tokens.push(val);
            }
          });
          value = tokens;
        }

        if (!value) {
          return;
        }

        const item = formatInfoItem(
          {
            key: intl.formatMessage(label),
            value: value,
          },
          intl,
          html,
          joinComma,
        );

        if (!item.value) {
          return;
        }

        info.push(item);
      },
    );
  }

  return info;
};

export default calculateKeyData;
